'use client';

import { CommandGroup, CommandItem } from '@/components/ui/command';
import { Loader } from '@/components/ui/loader';
import { api, useQuery } from '@/lib/convex';
import type { Id } from '@/convex/_generated/dataModel';
import { FolderKanban } from 'lucide-react';
import { useMemo } from 'react';
import { ItemIcon } from '../primitives';

/**
 * Lazy "Move to project →" sub-page. Only queries projects while mounted,
 * so thread/product pages don't pay for the list until the picker opens.
 */
export function ProjectPicker({
  term,
  heading,
  noneLabel,
  emptyLabel,
  orgSlug,
  onPick,
  onDone,
}: {
  term: string;
  heading: string;
  /** When set, renders a "remove from project" row that picks null. */
  noneLabel?: string;
  emptyLabel: string;
  orgSlug?: string;
  onPick: (folderId: Id<'threadFolders'> | null) => void | Promise<void>;
  onDone: () => void;
}) {
  const { data: projects, isPending } = useQuery(
    api.organizations.threads.folders.listProjects,
    orgSlug ? { orgSlug } : 'skip'
  );

  const filtered = useMemo(() => {
    const list: Array<{ _id: Id<'threadFolders'>; name: string }> =
      projects ?? [];
    const lower = term.trim().toLowerCase();
    if (!lower) return list;
    return list.filter((project) => project.name.toLowerCase().includes(lower));
  }, [projects, term]);

  const pick = (folderId: Id<'threadFolders'> | null) => {
    void Promise.resolve(onPick(folderId)).finally(onDone);
  };

  if (isPending && !projects) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader size="sm" intent="secondary" />
      </div>
    );
  }

  return (
    <CommandGroup heading={heading}>
      {noneLabel && !term.trim() && (
        <CommandItem value="project:none" onSelect={() => pick(null)}>
          <ItemIcon icon={FolderKanban} />
          <span className="min-w-0 flex-1 truncate">{noneLabel}</span>
        </CommandItem>
      )}
      {filtered.map((project) => (
        <CommandItem
          key={String(project._id)}
          value={`project:${String(project._id)}`}
          onSelect={() => pick(project._id)}
        >
          <ItemIcon icon={FolderKanban} />
          <span className="min-w-0 flex-1 truncate">{project.name}</span>
        </CommandItem>
      ))}
      {filtered.length === 0 && !noneLabel && (
        <div className="text-muted-foreground/60 px-3 py-4 text-center text-sm">
          {emptyLabel}
        </div>
      )}
    </CommandGroup>
  );
}
